import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Calendar,
  Search,
  Plus,
  Settings,
  Eye,
  FileText,
  Trash2,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Combobox } from '../components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { DataTable, DataTableColumnHeader } from '../components/ui/data-table';
import ApiService from '../services/api.service';
import useThaklisServicesOff from '../hooks/useThaklisServicesOff';
import useOrganizationSearch from '../hooks/useOrganizationSearch';
import { isPlatformLevel, can } from '../utils/roleUtils';
import { invoiceMoneyService } from '../services/accounting/invoiceMoney.service';
import { needsOutcome } from '../components/accounting/PaymentSourceFields';
import { serverMessage } from '../utils/apiConvert';
import { ProTableStats } from '../components/pro-table';
import StatementDialog from '../components/statement/StatementDialog';
import NewInvoiceDialog from '../components/invoices/NewInvoiceDialog';

const ALL_ORGANIZATIONS = { id: '', name: 'All organizations' };

const Invoices = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.message || null);
  const isPlatform = isPlatformLevel(currentUser?.role);
  // Thaklis-disabled TCs replace the ticket-based "From Tickets" flow with a plain Create Invoice that
  // just needs a mapped org (auto if the TC has exactly one, else picked). The invoice is a zero-line
  // DRAFT billed to that org; the user adds external lines on the detail page.
  const thaklisDisabled = useThaklisServicesOff(currentUser);
  const [createOrgPickerOpen, setCreateOrgPickerOpen] = useState(false);
  const [createMappedOrgs, setCreateMappedOrgs] = useState([]);

  const createBlankInvoice = async (org) => {
    const tcId = filters.typingCenterId || currentUser?.typingCenterId;
    try {
      const res = await ApiService.createInvoice({
        typingCenterId: tcId,
        organizationId: org.id,
        organizationName: org.name,
        organizationCode: org.code || ''
      });
      const created = res?.data?.data;
      setCreateOrgPickerOpen(false);
      if (created?.id) navigate(`/invoices/${created.id}`);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create invoice');
    }
  };

  const handleThaklisDisabledCreate = async () => {
    const tcId = filters.typingCenterId || currentUser?.typingCenterId;
    if (!tcId) return;
    try {
      const resp = await ApiService.get(`/api/mappings?typingCenterId=${tcId}`);
      const orgs = (resp.data?.data || []).map(m => ({
        id: m.organization_id, name: m.organization_name, code: m.organization_code || ''
      }));
      if (orgs.length === 1) {
        await createBlankInvoice(orgs[0]);
      } else if (orgs.length > 1) {
        setCreateMappedOrgs(orgs);
        setCreateOrgPickerOpen(true);
      } else {
        setError('No mapped organization found for this typing center.');
      }
    } catch (err) {
      setError('Failed to load mapped organizations');
    }
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    typingCenterId: '',
    organizationId: '',
    internalOrganizationId: '',
    groupId: ''
  });
  // Customer groups for the group filter — only shown when this TC actually has groups.
  const [orgGroups, setOrgGroups] = useState([]);
  useEffect(() => {
    let active = true;
    ApiService.getPortalCustomerGroups({ size: 100 })
      .then((res) => { if (active) setOrgGroups(res?.data?.data || res?.data || []); })
      .catch(() => { if (active) setOrgGroups([]); });
    return () => { active = false; };
  }, []);
  const [typingCenters, setTypingCenters] = useState([]);
  const {
    options: internalOrgs,
    loading: internalOrgsLoading,
    onInputChange: onInternalOrgInputChange
  } = useOrganizationSearch({
    typingCenterId: filters.typingCenterId && filters.typingCenterId !== 'all' ? filters.typingCenterId : null
  });
  // Held separately: the chosen customer may sit outside the current search results.
  const [selectedInternalOrg, setSelectedInternalOrg] = useState(null);
  const [statementOpen, setStatementOpen] = useState(false);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);

  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Optional From/To date filter, set via the calendar popup. Returns null when
  // unset → no date bound (all invoices).
  const getDateRange = () => {
    if (customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
      return { startDate, endDate };
    }
    return null;
  };

  useEffect(() => {
    fetchTypingCenters();
  }, []);

  useEffect(() => {
    if (filters.typingCenterId) {
      fetchInvoices();
      fetchStatistics();
    }
  }, [filters.status, filters.typingCenterId, filters.internalOrganizationId, filters.groupId, customStartDate, customEndDate, page, rowsPerPage]);

  // Reset the customer selection when the typing center changes.
  useEffect(() => {
    setSelectedInternalOrg(null);
    setFilters(prev => (prev.internalOrganizationId ? { ...prev, internalOrganizationId: '' } : prev));
  }, [filters.typingCenterId]);

  const fetchTypingCenters = async () => {
    try {
      const response = await ApiService.getTypingCenters();
      if (response.data?.data) {
        setTypingCenters(response.data.data);
        if (response.data.data.length > 0) {
          const defaultTc = currentUser?.typingCenterId || response.data.data[0].id;
          setFilters(prev => ({ ...prev, typingCenterId: defaultTc }));
        }
      }
    } catch (err) {
      console.error('Error fetching typing centers:', err);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const range = getDateRange();
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };
      if (range) {
        params.startDate = range.startDate.toISOString();
        params.endDate = range.endDate.toISOString();
      }
      if (filters.status) params.status = filters.status;
      if (filters.typingCenterId) params.typingCenterId = filters.typingCenterId;
      if (filters.organizationId) params.organizationId = filters.organizationId;
      if (filters.internalOrganizationId) params.internalOrganizationId = filters.internalOrganizationId;
      if (filters.groupId && filters.groupId !== 'all') params.groupId = filters.groupId;
      if (filters.search) params.search = filters.search;

      const response = await ApiService.getInvoices(params);
      if (response.data?.data) {
        setInvoices(response.data.data);
        setTotalCount(response.data.total ?? (response.data.totalPages || 1) * rowsPerPage);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const range = getDateRange();
      const response = await ApiService.getInvoiceStatistics(filters.typingCenterId,
        range && { startDate: range.startDate.toISOString(), endDate: range.endDate.toISOString() });
      if (response.data?.data) {
        setStatistics(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchInvoices();
  };

  const handleDownloadSummary = async () => {
    if (!filters.internalOrganizationId) return;
    try {
      const range = getDateRange();
      const params = {
        typingCenterId: filters.typingCenterId,
        internalOrganizationId: filters.internalOrganizationId,
      };
      if (range) {
        params.startDate = range.startDate.toISOString();
        params.endDate = range.endDate.toISOString();
      }
      if (filters.status && filters.status !== 'all') params.status = filters.status;

      const response = await ApiService.getInvoiceSummaryPdf(params);
      const blobUrl = window.URL.createObjectURL(response.data);
      const org = selectedInternalOrg;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Invoice-Summary-${(org?.name || 'org').replace(/[^a-z0-9]+/gi, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30_000);
    } catch (err) {
      console.error('Error downloading summary PDF:', err);
      setError('Failed to download invoice summary');
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await ApiService.getInvoicePdfView(invoiceId);
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30_000);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    // A draft whose government fee was paid needs "what happened to the money?" — that dialog is on the invoice.
    const paid = (await invoiceMoneyService.getFeePayments(invoiceId).catch(() => [])).filter(needsOutcome);
    if (paid.length > 0) {
      if (window.confirm('This draft has paid government fees. Open it to delete it and say what happened to the money?')) {
        navigate(`/invoices/${invoiceId}`);
      }
      return;
    }
    const confirmed = window.confirm('Are you sure you want to delete this draft invoice?');
    if (!confirmed) return;

    try {
      setLoading(true);
      await ApiService.deleteInvoice(invoiceId);
      await fetchInvoices();
      await fetchStatistics();
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(serverMessage(err, 'Failed to delete invoice'));
    } finally {
      setLoading(false);
    }
  };

  const getEffectiveStatus = (invoice) => {
    if (invoice.status === 'FINAL' && invoice.customer_payment_status === 'PAID') return 'PAID';
    if (invoice.status === 'FINAL' && invoice.customer_payment_status === 'PARTIAL') return 'PARTIALLY_PAID';
    return invoice.status;
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'DRAFT': return 'warning';
      case 'FINAL': return 'info';
      case 'PAID': return 'success';
      case 'PARTIALLY_PAID': return 'warning';
      case 'VOID': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      DRAFT: 'Draft',
      FINAL: 'Final',
      PAID: 'Paid',
      PARTIALLY_PAID: 'Partially Paid',
      VOID: 'Void',
    };
    return labels[status] || status;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const columns = [
    {
      accessorKey: 'invoice_number',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Invoice #" />,
      cell: ({ row }) => (
        <span className="text-primary">
          {row.original.invoice_number}
        </span>
      ),
    },
    {
      accessorKey: 'organization_name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Organization" />,
      cell: ({ row }) => {
        const customerOrg = row.original.internal_organization_name;
        const thaklisOrg = row.original.organization_name;
        const displayName = customerOrg || thaklisOrg;
        const displayCode = row.original.organization_code;
        return (
          <div>
            <div>{displayName}</div>
            {displayCode && (
              <div className="text-[11px] leading-tight text-muted-foreground">{displayCode}</div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'invoice_date',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => formatDate(row.original.invoice_date),
    },
    {
      accessorKey: 'item_count',
      header: 'Items',
      cell: ({ row }) => (
        <span className="text-center block">{row.original.item_count}</span>
      ),
    },
    {
      accessorKey: 'grand_total',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Total" className="text-right" />,
      cell: ({ row }) => (
        <span className="font-medium text-right block">
          {formatCurrency(row.original.grand_total)}
        </span>
      ),
    },
    {
      accessorKey: 'internal_total',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Cost" className="text-right" />,
      cell: ({ row }) => {
        const internal = row.original.internal_total;
        if (!internal) return <span className="text-right block text-muted-foreground">—</span>;
        return (
          <span className="text-right block text-muted-foreground">
            {formatCurrency(internal)}
          </span>
        );
      },
    },
    {
      id: 'profit',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Profit" className="text-right" />,
      cell: ({ row }) => {
        const internal = row.original.internal_total;
        const grand = row.original.grand_total;
        if (!internal) return <span className="text-right block text-muted-foreground">—</span>;
        const profit = (grand || 0) - internal;
        return (
          <span className={`font-semibold text-right block ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(profit)}
          </span>
        );
      },
    },
    {
      id: 'margin',
      header: 'Margin %',
      cell: ({ row }) => {
        const internal = row.original.internal_total;
        const grand = row.original.grand_total;
        if (!internal || !grand) return <span className="text-right block text-muted-foreground">—</span>;
        const profit = grand - internal;
        const margin = (profit / grand) * 100;
        return (
          <span className={`text-right block ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {margin.toFixed(1)}%
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const effective = getEffectiveStatus(row.original);
        return (
          <Badge variant={getStatusVariant(effective)} className="text-xs font-medium">
            {getStatusLabel(effective)}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/invoices/${row.original.id}`)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.original.status !== 'DRAFT' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDownloadPdf(row.original.id, row.original.invoice_number)}
            >
              <FileText className="h-4 w-4" />
            </Button>
          )}
          {row.original.status === 'DRAFT' && can(currentUser, 'invoice', 'delete') && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => handleDeleteInvoice(row.original.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const disablePrev = page === 0;
  const disableNext = page >= totalPages - 1;

  return (
    <div className="space-y-3" style={{ width: 0, minWidth: '100%', fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' }}>
      {/* Mapped-org picker for the Thaklis-disabled "Create Invoice" flow (only when >1 mapping). */}
      <Dialog open={createOrgPickerOpen} onOpenChange={setCreateOrgPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select organization to bill</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {createMappedOrgs.map(org => (
              <Button
                key={org.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => createBlankInvoice(org)}
              >
                {org.name}{org.code ? ` (${org.code})` : ''}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {notice && (
        <Alert>
          <AlertDescription className="flex items-center justify-between gap-2">
            {notice}
            <button type="button" className="text-sm underline" onClick={() => setNotice(null)}>Dismiss</button>
          </AlertDescription>
        </Alert>
      )}

      {/* Statistics */}
      <ProTableStats
        loading={!statistics}
        cards={[
          { key: 'all', label: 'Total Invoices', value: statistics?.total || 0 },
          { key: 'DRAFT', label: 'Drafts', value: statistics?.draft_count || 0, color: 'warning.main' },
          { key: 'FINAL', label: 'Finalized', value: statistics?.final_count || 0, color: 'info.main' },
          { key: 'PAID', label: 'Paid', value: statistics?.paid_count || 0, color: 'success.main' },
        ].map((c) => ({
          ...c,
          highlight: filters.status === c.key,
          onClick: () => { setPage(0); setFilters((prev) => ({ ...prev, status: c.key })); },
        }))}
      />

      <NewInvoiceDialog
        open={newInvoiceOpen}
        onClose={() => setNewInvoiceOpen(false)}
        typingCenterId={filters.typingCenterId && filters.typingCenterId !== 'all' ? filters.typingCenterId : null}
        thaklisEnabled={!thaklisDisabled}
        onBlank={handleThaklisDisabledCreate}
      />

      <StatementDialog
        open={statementOpen}
        onClose={() => setStatementOpen(false)}
        typingCenterId={filters.typingCenterId && filters.typingCenterId !== 'all' ? filters.typingCenterId : null}
        initialOrg={selectedInternalOrg?.id ? { id: selectedInternalOrg.id, name: selectedInternalOrg.name } : null}
        initialRange={customStartDate && customEndDate ? { from: customStartDate, to: customEndDate } : null}
      />

      {/* Custom Date Dialog */}
      <Dialog open={customDateOpen} onOpenChange={setCustomDateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Date Range</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomDateOpen(false)}>
              Cancel
            </Button>
            {(customStartDate || customEndDate) && (
              <Button
                variant="outline"
                onClick={() => {
                  setCustomStartDate('');
                  setCustomEndDate('');
                  setCustomDateOpen(false);
                  setPage(0);
                }}
              >
                Clear
              </Button>
            )}
            <Button
              onClick={() => {
                if (customStartDate && customEndDate) {
                  setCustomDateOpen(false);
                  setPage(0);
                }
              }}
              disabled={!customStartDate || !customEndDate}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters — one row, like the other list pages */}
      <div className="flex gap-2 flex-wrap items-center" style={{ marginBottom: 12 }}>
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Invoice # or organization..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-8 h-8 text-[13px]"
                />
              </div>
            </div>

            {/* Typing Center Filter (Admin Only) */}
            {isPlatform && (
              <div className="">
                <Select
                  value={filters.typingCenterId}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, typingCenterId: value }))}
                >
                  <SelectTrigger className="w-[180px] h-8 text-[13px]">
                    <SelectValue placeholder="Select center" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Centers</SelectItem>
                    {typingCenters.map(tc => (
                      <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Status Filter */}
            <div className="">
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-[140px] h-8 text-[13px]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="FINAL">Final</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                  <SelectItem value="VOID">Void</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Group Filter — only when this TC has customer groups */}
            {orgGroups.length > 0 && (
              <div className="">
                <Select
                  value={filters.groupId || 'all'}
                  onValueChange={(value) => { setPage(0); setFilters(prev => ({ ...prev, groupId: value })); }}
                >
                  <SelectTrigger className="w-[160px] h-8 text-[13px]">
                    <SelectValue placeholder="All Groups" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Groups</SelectItem>
                    {orgGroups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Customer Organization Filter */}
            {filters.typingCenterId && filters.typingCenterId !== 'all' && (
              <div className="">
                <Combobox
                  className="w-[200px]"
                  triggerClassName="h-8 py-1 text-[13px]"
                  options={[ALL_ORGANIZATIONS].concat(internalOrgs)}
                  value={selectedInternalOrg || ALL_ORGANIZATIONS}
                  onChange={(org) => {
                    const next = org || ALL_ORGANIZATIONS;
                    setSelectedInternalOrg(next.id ? next : null);
                    setFilters(prev => ({ ...prev, internalOrganizationId: next.id || '' }));
                  }}
                  onSearchChange={(text) => onInternalOrgInputChange(null, text, 'input')}
                  getOptionLabel={(o) => o?.name || ''}
                  getOptionKey={(o) => o?.id || 'all'}
                  loading={internalOrgsLoading}
                  placeholder="All organizations"
                  searchPlaceholder="Search by name or TRN..."
                  emptyText="No organizations found"
                />
              </div>
            )}

            {/* Right side: count, date, summary, settings, create */}
            <div className="flex gap-1 items-center ml-auto">
              <span className="text-xs text-muted-foreground whitespace-nowrap mr-2">{totalCount} invoices</span>
              {/* Date filter — opens the From/To popup. Highlighted when a range is set. */}
              <Button
                variant={customStartDate && customEndDate ? 'default' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setCustomDateOpen(true)}
                title={customStartDate && customEndDate ? `Date: ${customStartDate} to ${customEndDate}` : 'Filter by date range'}
              >
                <Calendar className="h-4 w-4" />
              </Button>
              {/* Wrap in a span so the hover tooltip still shows while the button is
                  disabled (disabled buttons don't fire hover / show their own title). */}
              <span
                className="inline-block"
                title={filters.internalOrganizationId ? 'Download the invoice list PDF for this customer' : 'Select a customer organization first'}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!filters.internalOrganizationId}
                  onClick={handleDownloadSummary}
                  className={`h-8 text-[13px] ${!filters.internalOrganizationId ? 'pointer-events-none' : ''}`}
                >
                  <FileText className="h-4 w-4 mr-1" /> Invoice list PDF
                </Button>
              </span>
              {can(currentUser, 'finance', 'read') && (
                <Button variant="ghost" size="sm" className="h-8 text-[13px]" title="Customer statement: invoices, payments and balance"
                  disabled={!filters.typingCenterId || filters.typingCenterId === 'all'}
                  onClick={() => setStatementOpen(true)}>
                  <FileText className="h-4 w-4 mr-1" /> Statement
                </Button>
              )}
              {can(currentUser, 'settings', 'read') && (
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Invoice settings"
                  onClick={() => navigate('/settings?section=documents&tab=invoices')}>
                  <Settings className="h-4 w-4" />
                </Button>
              )}
              {can(currentUser, 'invoice', 'write') && (
                <Button size="sm" className="h-8 text-[13px]" onClick={() => setNewInvoiceOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  New Invoice
                </Button>
              )}
            </div>
      </div>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            data={invoices}
            loading={loading}
            showPagination={false}
            showSearch={false}
            emptyMessage="No invoices found"
            branded
            onRowClick={(row) => navigate(`/invoices/${row.id}`)}
          />

          {/* Custom Pagination */}
          <div className="flex items-center justify-between p-4 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page:</span>
              <Select
                value={`${rowsPerPage}`}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-[70px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={disablePrev}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={disableNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Invoices;
